import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, ArrowRight, Laptop, Layers, BookOpen, Wrench, HelpCircle, GitBranch, Terminal } from 'lucide-react';
import contentData from '../data/website_content.json';
import { useRouter } from '../context/RouterContext';
import { TOOLS_DATA } from '../data/toolsData';
import { TOPICAL_PAGES } from '../data/topicalLandingData';

interface SearchItem {
  id: string;
  title: string;
  snippet: string;
  category: 'Products' | 'Features' | 'Tools' | 'Tutorials' | 'Journal' | 'FAQ' | 'Updates';
  targetPath: string;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { navigate } = useRouter();

  // Index all content from website_content.json and dynamic catalogs
  const searchIndex: SearchItem[] = [
    // Tools Directory items
    ...TOOLS_DATA.map(t => ({
      id: `tool-dir-${t.slug}`,
      title: t.name,
      snippet: t.shortDescription,
      category: 'Tools' as const,
      targetPath: `/tools/${t.slug}`
    })),
    // Topical Architecture & SEO Guides
    ...Object.values(TOPICAL_PAGES).map(p => ({
      id: `topical-${p.slug}`,
      title: p.title,
      snippet: p.summary,
      category: 'Features' as const,
      targetPath: `/${p.slug}`
    })),
    // Product & Architecture
    {
      id: 'prod-main',
      title: 'Lashary FB Market Suite Overview',
      snippet: 'Professional Windows desktop automation workstation for Facebook Marketplace with native 64-bit Fluent shell and high-speed native browser automation.',
      category: 'Products',
      targetPath: '/product'
    },
    {
      id: 'prod-arch',
      title: 'Workstation Architecture & Local Security',
      snippet: 'Windows Fluent shell, Direct Native Automation Engine, hardware-backed vault, high-speed local storage.',
      category: 'Products',
      targetPath: '/product#architecture'
    },
    {
      id: 'prod-proxy',
      title: 'Proxy Center (HTTP & SOCKS5 Routing)',
      snippet: 'Verified residential & datacenter proxy integration with local ProxyBridge loopback tunnel.',
      category: 'Features',
      targetPath: '/features/proxy'
    },
    // Pipeline & Features
    {
      id: 'feat-pipeline',
      title: '2-Phase Review-First Automation Pipeline',
      snippet: '8-stage pipeline: Pre-flight → Image allocation → AI Content → Phase 1 Draft → ID Interception → Reconciliation → Location → Phase 2 Publish.',
      category: 'Features',
      targetPath: '#pipeline'
    },
    {
      id: 'feat-allocator',
      title: 'Deterministic Unique-First Image Allocator',
      snippet: 'Mathematical folder slot formula preventing duplicate hero image flags across multi-listing queues.',
      category: 'Features',
      targetPath: '/features#allocator'
    },
    {
      id: 'feat-isolation',
      title: 'Slot-Isolated Error & Checkpoint Handling',
      snippet: 'Quarantines individual listing errors or Facebook account checkpoints without crashing the rest of your batch.',
      category: 'Features',
      targetPath: '#isolation'
    },
    {
      id: 'feat-autoreply',
      title: 'AI Auto-Reply & 5-Tab Messenger CRM',
      snippet: 'Multi-turn Gemini 2.5 Flash chatbot with automated buyer intent classification, Pakistani & US phone harvesting, and approval mode.',
      category: 'Features',
      targetPath: '/features'
    },
    {
      id: 'feat-delete-relist',
      title: 'FB Auto-Delete & Relist (Mutated Spintax Engine)',
      snippet: 'Automated clean removal of stale Marketplace items with automatic relisting via mutated spintax and unique image variations.',
      category: 'Features',
      targetPath: '/features'
    },
    {
      id: 'feat-webhooks',
      title: 'Remote Webhook Alerts (Telegram Bot & Discord)',
      snippet: 'Instant push notifications to Telegram and Discord when batches finish, account checkpoints trigger, or buyer leads arrive.',
      category: 'Features',
      targetPath: '/features'
    },
    {
      id: 'feat-analytics',
      title: '30-Day Conversion & Funnel Analytics',
      snippet: 'Real-time dashboard KPI tracking views, inquiries, phone numbers captured, and view-to-lead conversion rates.',
      category: 'Features',
      targetPath: '/features'
    },
    {
      id: 'feat-security',
      title: 'Hardware-Backed Credential Security & Privacy',
      snippet: 'Operating system hardware encryption protecting passwords and session cookies bound strictly to your Windows PC.',
      category: 'Features',
      targetPath: '/features#security'
    },
    {
      id: 'feat-telemetry',
      title: 'Real-Time Hardware Telemetry Monitor',
      snippet: 'Real-time host hardware workload monitoring to maintain smooth pacing and prevent memory leaks.',
      category: 'Features',
      targetPath: '/features#telemetry'
    },
    // Free Tools
    ...contentData.freeTools.map((tool) => ({
      id: `tool-${tool.id}`,
      title: tool.name,
      snippet: tool.description,
      category: 'Tools' as const,
      targetPath: `/tools#${tool.id}`
    })),
    // Tutorials
    ...contentData.tutorials.map((tut) => ({
      id: `tut-${tut.id}`,
      title: tut.title,
      snippet: tut.overview,
      category: 'Tutorials' as const,
      targetPath: `/tutorials#${tut.id}`
    })),
    // Journal
    ...contentData.journal.map((art) => ({
      id: `art-${art.id}`,
      title: art.title,
      snippet: art.summary,
      category: 'Journal' as const,
      targetPath: `/journal#${art.id}`
    })),
    // FAQ
    ...contentData.faq.slice(0, 8).map((f, idx) => ({
      id: `faq-${idx}`,
      title: f.q,
      snippet: f.a,
      category: 'FAQ' as const,
      targetPath: '#faq'
    })),
    // Updates
    {
      id: 'update-v15',
      title: 'Lashary v1.5.0 - Flutter Workstation Release',
      snippet: 'Specialized Vehicle & Property Workspaces, ID Warmup Anti-Ban Simulator, Engagement Automation, Data Scraper, Campaign Scheduler, Hardware-Backed Sandboxing.',
      category: 'Updates',
      targetPath: '/updates'
    },
    {
      id: 'update-v11',
      title: 'Lashary V1.1 Production Release Notes',
      snippet: 'Windows native desktop shell, high-performance local storage, AI listing generation, voice status alerts, pre-flight dry runs.',
      category: 'Updates',
      targetPath: '/updates'
    }
  ];

  const filteredResults = query.trim() === ''
    ? searchIndex.slice(0, 6)
    : searchIndex.filter((item) => {
        const q = query.toLowerCase();
        return item.title.toLowerCase().includes(q) || item.snippet.toLowerCase().includes(q) || item.category.toLowerCase().includes(q);
      });

  const handleSelect = useCallback(
    (item: SearchItem) => {
      onClose();
      navigate(item.targetPath);
    },
    [navigate, onClose]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % Math.max(1, filteredResults.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredResults.length) % Math.max(1, filteredResults.length));
      } else if (e.key === 'Enter' && filteredResults[selectedIndex]) {
        e.preventDefault();
        handleSelect(filteredResults[selectedIndex]);
      }
    },
    [isOpen, onClose, filteredResults, selectedIndex, handleSelect]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case 'Products': return <Laptop className="w-3.5 h-3.5 text-rose" />;
      case 'Features': return <Layers className="w-3.5 h-3.5 text-apricot" />;
      case 'Tools': return <Wrench className="w-3.5 h-3.5 text-gold" />;
      case 'Tutorials': return <BookOpen className="w-3.5 h-3.5 text-jade" />;
      case 'Journal': return <Terminal className="w-3.5 h-3.5 text-orchid" />;
      case 'Updates': return <GitBranch className="w-3.5 h-3.5 text-teal" />;
      default: return <HelpCircle className="w-3.5 h-3.5 text-muted" />;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4 bg-obsidian/85 backdrop-blur-xl animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl bg-charcoal border border-white/10 rounded-3xl shadow-2xl shadow-black/90 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input Header */}
        <div className="flex items-center px-5 py-4 border-b border-white/10 gap-3">
          <Search className="w-5 h-5 text-rose shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder="Search products, features, free tools, tutorials, journal..."
            className="w-full bg-transparent text-sm sm:text-base text-ivory placeholder:text-muted focus:outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 rounded-lg text-muted hover:text-ivory"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:inline-block text-[10px] font-mono px-2 py-0.5 rounded bg-plum-black text-muted border border-white/10">
            ESC
          </kbd>
        </div>

        {/* Search Results List */}
        <div className="max-h-[60vh] overflow-y-auto p-3 space-y-1 divide-y divide-white/5">
          {filteredResults.length === 0 ? (
            <div className="py-12 text-center text-muted text-xs font-mono">
              No results found for "{query}". Try searching for "proxy", "image allocator", "security", or "draft".
            </div>
          ) : (
            filteredResults.map((item, idx) => {
              const isSelected = idx === selectedIndex;
              return (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`w-full text-left p-3.5 rounded-2xl transition-all flex items-center justify-between gap-3 ${
                    isSelected
                      ? 'bg-plum-black/90 text-ivory shadow-md ring-1 ring-rose/40'
                      : 'hover:bg-plum-black/40 text-soft-white'
                  }`}
                >
                  <div className="space-y-1 flex-1 pr-2">
                    <div className="flex items-center gap-2">
                      <span className="p-1 rounded-md bg-obsidian border border-white/10 flex items-center">
                        {getCategoryIcon(item.category)}
                      </span>
                      <span className="text-xs font-mono font-bold text-rose">
                        {item.category}
                      </span>
                      <span className="text-xs font-bold text-ivory truncate">
                        {item.title}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted line-clamp-1 leading-relaxed">
                      {item.snippet}
                    </p>
                  </div>

                  <ArrowRight className={`w-4 h-4 shrink-0 transition-transform ${
                    isSelected ? 'text-rose translate-x-1' : 'text-muted/60'
                  }`} />
                </button>
              );
            })
          )}
        </div>

        {/* Command Palette Footer */}
        <div className="px-5 py-3 border-t border-white/10 bg-obsidian/80 flex items-center justify-between text-[11px] text-muted font-mono">
          <div className="flex items-center gap-3">
            <span>↑↓ Navigate</span>
            <span>↵ Select</span>
            <span>ESC Close</span>
          </div>
          <span className="text-rose/80">Lashary Universal Search</span>
        </div>
      </div>
    </div>
  );
};
